<template>
  <div v-if="sections" style="overflow-x: hidden">
    <!-- voting -->
    <v-alert v-if="sections.length == 0" outlined type="error" text>
      Alert!<br />
      No voting data available.
    </v-alert>

    <v-stepper v-model="step" flat>
      <!-- sections with options -->
      <v-stepper-content v-for="(s, i) in sections" :key="i" :step="i">
        <div v-bind="headerProps">
          {{ s.description }}
        </div>

        <v-row v-if="s.options && s.options.length > 0">
          <v-col
            class="mx-auto"
            cols="12"
            md="6"
            v-for="(o, j) in s.options"
            :key="j"
          >
            <v-card
              outlined
              :class="{ emphasis: chosen[s.id] === o.id }"
              class="mx-auto"
              width="20rem"
            >
              <v-img
                :src="`${
                  o.image_url ||
                  'https://avatars.dicebear.com/api/initials/' +
                    (o.description ? o.description.split(' ')[0] : 'P') +
                    '.svg'
                }`"
                class="white"
                :height="
                  o.image_url && o.image_url.length == 0 ? '0rem' : '20rem'
                "
                style="background-position: 200% 0"
              >
                <v-overlay v-if="chosen[s.id] === o.id" absolute :opacity="0.2">
                  <v-icon size="72">mdi-check</v-icon>
                </v-overlay>
              </v-img>
              <v-card-title class="text-uppercase text-center d-block">{{
                o.description
              }}</v-card-title>

              <v-card-actions>
                <v-btn
                  v-if="o.sub_options.length == 0"
                  v-bind="btnProps"
                  block
                  @click="choose(i, s, o)"
                  class="primary"
                  :disabled="chosen[s.id] === o.id"
                  >{{ chosen[s.id] === o.id ? "selected" : "select" }}</v-btn
                >
                <v-row v-else>
                  <v-col v-for="(so, k) in o.sub_options" :key="`so-${k}`">
                    <v-btn
                      v-bind="btnProps"
                      block
                      @click="
                        choose(i, s, {
                          ...o,
                          id: so.id,
                          description: `${o.description} (${so.description})`,
                        })
                      "
                      class="primary"
                      :disabled="chosen[s.id] === so.id"
                      >{{ so.description }}</v-btn
                    >
                  </v-col></v-row
                >
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-stepper-content>
    </v-stepper>
    <!-- </v-col> -->

    <!-- confirmation dialog -->
    <v-dialog v-model="dialog" scrollable width="400px">
      <v-card>
        <v-card-title>Confirm Vote</v-card-title>
        <v-divider></v-divider>

        <v-card-text style="height: 300px">
          <v-list two-line>
            <template v-for="(item, index) in overview">
              <v-list-item :key="index" class="pa-0 ma-0">
                <v-list-item-avatar tile color="grey lighten-1" :size="45">
                  <v-img
                    :src="`${
                      item.choice.image_url ||
                      'https://avatars.dicebear.com/api/initials/' +
                        (item.choice.description
                          ? item.choice.description.split(' ')[0]
                          : 'P') +
                        '.svg'
                    }`"
                  ></v-img>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.choice.description || "N/A" }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="grey--text">
                    {{ item.section.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer />
          <v-btn depressed text @click="dialog = false">cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="!valid || isVoting"
            :loading="isVoting"
            @click="vote"
            type="submit"
          >
            vote
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-bottom-navigation fixed app>
      <v-btn
        color="primary"
        text
        @click="nextStep(-1)"
        :disabled="sections.length == 0 || step == 0"
      >
        <span>PREV</span>

        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn
        color="primary"
        text
        class="mx-10"
        @click="dialog = true"
        :disabled="sections.length == 0 || !canFinish"
      >
        <span>FINISH</span>

        <v-icon>mdi-thumb-up</v-icon>
      </v-btn>

      <v-btn
        color="primary"
        text
        @click="nextStep(1)"
        :disabled="sections.length == 0 || step == sections.length - 1"
      >
        <span>NEXT</span>

        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-bottom-navigation>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { rules, fieldProps, btnProps } from "@/helpers";

export default {
  data: () => ({
    rules,
    fieldProps,
    btnProps: { ...btnProps, large: true },

    headerProps: {
      class: "text-center mb-8 font-weight-bold text-uppercase",
      style: "font-size: 1.5rem;",
    },

    actionDivProps: {
      class: "d-flex justify-space-between mt-10 flex-wrap",
    },

    overlay: false,
    dialog: false,
    valid: true,
    indexNumber: "",

    step: 0,
    isVoting: false,

    chosen: {},
    overview: null,
  }),

  computed: {
    sections() {
      const poll = this.$store.getters.getCollection("poll");
      return poll ? poll.sections : [];
    },

    poll() {
      return this.$store.getters.getCollection("poll");
    },

    indexNumberConfirmationRule() {
      const { index_number } = JSON.parse(localStorage.getItem("usr"));
      return [
        (v) => !!v || "Enter index number.",
        (v) => v === index_number || "Wrong index number.",
      ];
    },

    canFinish() {
      return (
        this.chosen &&
        this.sections &&
        Object.keys(this.chosen).length == this.sections.length
      );
    },
  },

  methods: {
    ...mapActions(["makeFetch"]),

    nextStep(n) {
      if (this.step == 0 && n < 0) return;
      else if (this.step == this.sections.length - 1 && n > 0) return;
      else this.step = this.step + n;
    },

    startVoting(resetOverview) {
      this.step = 0;
      this.chosen = {};
      if (resetOverview)
        this.overview = this.sections.map((section) => {
          const { description, id } = section;
          return { section: { description, id }, choice: {} };
        });
    },

    choose(sectionIndex, sectionObj, optionObj) {
      // update selection
      const prevChosen = { ...this.chosen };
      prevChosen[sectionObj.id] = optionObj.id;
      this.chosen = { ...prevChosen };

      // update overview
      const prevOverview = [...this.overview];
      const { description, id } = sectionObj;
      prevOverview[sectionIndex] = {
        section: { description, id },
        choice: optionObj,
      };
      this.overview = [...prevOverview];
    },

    async vote() {
      this.isVoting = true;
      const votes = [];
      for (const key in this.chosen) {
        votes.push({
          section_id: key,
          option_id: this.chosen[key],
        });
      }

      const res = await this.makeFetch({
        method: "post",
        goTo: `/polls/${this.$route.params.id}`,
        way: "/votes",
        data: { votes: votes },
      })
        .catch(console.error)
        .finally(() => {
          this.isVoting = false;
          this.dialog = false;
        });

      this.$router.push({
        name: "VotePage",
        params: { id: this.$route.params.id },
        query: {
          status: res.success ? 200 : 401,
        },
      });
    },
  },

  watch: {
    sections() {
      this.startVoting(true);
    },
  },

  mounted() {
    if (this.sections) this.startVoting(true);
  },
};
</script>

<style>
.emphasis {
  border: 3px #111111 !important;
}
</style>
